import { useState } from 'react';
import { useGetTokensQuery } from '../store/adminApi';
import { Card } from '../components/Card';
import { Table, Column } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { FilterInput } from '../components/FilterInput';
import { ErrorState } from '../components/LoadingSpinner';
import { StatusBadge, getPlatformStatusType } from '../components/StatusBadge';
import type { TokenListItem, TokenQueryParams } from '../types/api';
import { RelativeTime } from '../components/admin/TableCells';
import { PageHeader, TableSkeleton } from '../components/admin/AdminUI';

const LIMIT = 50;

export function TokensPage() {
  const [filters, setFilters] = useState<TokenQueryParams>({
    limit: LIMIT,
    offset: 0,
  });

  const { data, isLoading, error } = useGetTokensQuery(filters);

  const updateFilter = (key: keyof TokenQueryParams, value: string) => {
    setFilters((prev: TokenQueryParams) => ({
      ...prev,
      [key]: value || undefined,
      offset: 0, // Reset pagination on filter change
    }));
  };

  const columns: Column<TokenListItem>[] = [
    {
      key: 'platform',
      header: 'Platform',
      render: (row) => (
        <StatusBadge
          status={getPlatformStatusType(row.platform)}
          label={row.platform.toUpperCase()}
        />
      ),
    },
    { key: 'appId', header: 'App ID' },
    { key: 'workspaceId', header: 'Workspace', className: 'font-mono text-xs' },
    { key: 'userId', header: 'User', className: 'font-mono text-xs' },
    { key: 'deviceId', header: 'Device ID', className: 'font-mono text-xs' },
    {
      key: 'appVersion',
      header: 'Version',
      render: (row) => row.appVersion || '-',
    },
    {
      key: 'lastSeenAt',
      header: 'Last Seen',
      render: (row) => <RelativeTime iso={row.lastSeenAt} />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => <RelativeTime iso={row.createdAt} never="—" />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Push Tokens"
        subtitle={<>View registered device tokens (raw token values are never shown)</>}
      />

      {/* Filters */}
      <Card title="Filters">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <FilterInput
            label="Workspace ID"
            value={filters.workspaceId || ''}
            onChange={(v) => updateFilter('workspaceId', v)}
            placeholder="UUID"
          />
          <FilterInput
            label="User ID"
            value={filters.userId || ''}
            onChange={(v) => updateFilter('userId', v)}
            placeholder="UUID"
          />
          <FilterInput
            label="App ID"
            value={filters.appId || ''}
            onChange={(v) => updateFilter('appId', v)}
            placeholder="e.g., levelup"
          />
          <FilterInput
            label="Platform"
            value={filters.platform || ''}
            onChange={(v) => updateFilter('platform', v)}
            type="select"
            options={[
              { value: 'ios', label: 'iOS' },
              { value: 'android', label: 'Android' },
            ]}
          />
          <FilterInput
            label="Device ID"
            value={filters.deviceId || ''}
            onChange={(v) => updateFilter('deviceId', v)}
            placeholder="Device identifier"
          />
        </div>
      </Card>

      {/* Results */}
      <Card>
        {isLoading ? (
          <TableSkeleton message="Loading tokens..." />
        ) : error ? (
          <ErrorState message="Failed to load tokens" />
        ) : (
          <>
            <Table<TokenListItem>
              columns={columns}
              data={data?.tokens || []}
              keyField="id"
              emptyMessage="No tokens found matching the filters"
            />
            {data && (
              <Pagination
                total={data.pagination.total}
                limit={data.pagination.limit}
                offset={data.pagination.offset}
                onPageChange={(offset) =>
                  setFilters((prev: TokenQueryParams) => ({ ...prev, offset }))
                }
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
